import React, { createContext, useState, useCallback, useRef, useEffect, useContext, ReactNode } from 'react';
import { ErrorNotification, ErrorResponse, ErrorCode } from '../types/error';
import { 
  LOGOUT_DELAY_MS, 
  ERROR_NOTIFICATION_DURATION_MS, 
  WARNING_NOTIFICATION_DURATION_MS, 
  INFO_NOTIFICATION_DURATION_MS
} from '../api/common';

interface ErrorNotificationContextType {
  notifications: ErrorNotification[];
  showError: (title: string, message: string, details?: string, autoClose?: boolean, duration?: number) => string;
  showWarning: (title: string, message: string, details?: string, autoClose?: boolean, duration?: number) => string;
  showInfo: (title: string, message: string, details?: string, autoClose?: boolean, duration?: number) => string;
  showSuccess: (title: string, message: string, details?: string, autoClose?: boolean, duration?: number) => string;
  showApiError: (errorResponse: ErrorResponse) => void;
  removeNotification: (id: string) => void;
  clearAllNotifications: () => void;
  updateNotificationDuration: (id: string, duration: number) => void;
}

const ErrorNotificationContext = createContext<ErrorNotificationContextType | undefined>(undefined);

interface ErrorNotificationProviderProps {
  children: ReactNode;
}

export const ErrorNotificationProvider: React.FC<ErrorNotificationProviderProps> = ({ children }) => {
  const [notifications, setNotifications] = useState<ErrorNotification[]>([]);
  const timeoutRefs = useRef<Map<string, NodeJS.Timeout>>(new Map());

  const removeNotification = useCallback((id: string) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
    const timeout = timeoutRefs.current.get(id);
    if (timeout) {
      clearTimeout(timeout);
      timeoutRefs.current.delete(id);
    }
  }, []);

  const createNotification = useCallback((
    type: 'error' | 'warning' | 'info' | 'success',
    title: string,
    message: string,
    details?: string,
    autoClose: boolean = true,
    duration: number = ERROR_NOTIFICATION_DURATION_MS
  ) => {
    const notification: ErrorNotification = {
      id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
      type,
      title,
      message,
      details,
      timestamp: new Date(),
      autoClose,
      duration
    };
    
    setNotifications(prev => [...prev, notification]);
    
    if (autoClose) {
      const timeout = setTimeout(() => {
        setNotifications(prev => prev.filter(n => n.id !== notification.id));
        timeoutRefs.current.delete(notification.id);
      }, duration);
      
      timeoutRefs.current.set(notification.id, timeout);
    }
    
    return notification.id;
  }, []);

  const showError = useCallback((
    title: string, 
    message: string, 
    details?: string,
    autoClose: boolean = true,
    duration: number = ERROR_NOTIFICATION_DURATION_MS
  ) => {
    return createNotification('error', title, message, details, autoClose, duration);
  }, [createNotification]);

  const showWarning = useCallback((
    title: string, 
    message: string, 
    details?: string,
    autoClose: boolean = true,
    duration: number = WARNING_NOTIFICATION_DURATION_MS
  ) => {
    return createNotification('warning', title, message, details, autoClose, duration);
  }, [createNotification]);

  const showInfo = useCallback((
    title: string, 
    message: string, 
    details?: string,
    autoClose: boolean = true,
    duration: number = INFO_NOTIFICATION_DURATION_MS
  ) => {
    return createNotification('info', title, message, details, autoClose, duration);
  }, [createNotification]);

  const showSuccess = useCallback((
    title: string, 
    message: string, 
    details?: string,
    autoClose: boolean = true,
    duration: number = INFO_NOTIFICATION_DURATION_MS
  ) => {
    return createNotification('success', title, message, details, autoClose, duration);
  }, [createNotification]);

  const showApiError = useCallback((errorResponse: ErrorResponse) => {
    const { error, message, details } = errorResponse;
    
    switch (error) {
      case ErrorCode.UNAUTHORIZED:
        showError('Session Expired', message, details);
        setTimeout(() => {
          localStorage.removeItem('ACCESS_TOKEN');
          localStorage.removeItem('PROFILE');
          window.location.href = '/';
        }, LOGOUT_DELAY_MS);
        return;
        
      case ErrorCode.FORBIDDEN:
        showError('Access Denied', message, details);
        break;
        
      case ErrorCode.VALIDATION_ERROR:
      case ErrorCode.BINDING_ERROR:
      case ErrorCode.CONSTRAINT_VIOLATION:
        showError('Validation Error', message, details);
        break;
        
      case ErrorCode.NOT_FOUND:
        showWarning('Not Found', message, details);
        break;
        
      case ErrorCode.INTERNAL_SERVER_ERROR:
        showError('Server Error', message, details);
        break;
        
      case ErrorCode.BAD_REQUEST:
      case ErrorCode.MISSING_PARAMETER:
      case ErrorCode.TYPE_MISMATCH:
      case ErrorCode.MALFORMED_REQUEST:
      case ErrorCode.INVALID_ARGUMENT:
      case ErrorCode.INVALID_SEARCH:
      case ErrorCode.METHOD_NOT_ALLOWED:
        showError('Bad Request', message, details);
        break;
        
      case ErrorCode.NETWORK_ERROR:
        showError('Network Error', message, details);
        break;
        
      default:
        showError('Error', message, details);
    }
  }, [showError, showWarning]);

  const clearAllNotifications = useCallback(() => {
    timeoutRefs.current.forEach(timeout => clearTimeout(timeout));
    timeoutRefs.current.clear();
    setNotifications([]);
  }, []);

  const updateNotificationDuration = useCallback((id: string, duration: number) => {
    setNotifications(prev => 
      prev.map(n => 
        n.id === id 
          ? { ...n, duration, autoClose: true }
          : n
      )
    );
    const existingTimeout = timeoutRefs.current.get(id);
    if (existingTimeout) {
      clearTimeout(existingTimeout);
    }
    const timeout = setTimeout(() => {
      removeNotification(id);
    }, duration);
    timeoutRefs.current.set(id, timeout);
  }, [removeNotification]);

  useEffect(() => {
    const currentTimeoutRefs = timeoutRefs.current;
    return () => {
      currentTimeoutRefs.forEach(timeout => clearTimeout(timeout));
      currentTimeoutRefs.clear();
    };
  }, []);

  useEffect(() => {
    const cleanup = () => {
      timeoutRefs.current.forEach(timeout => clearTimeout(timeout));
      timeoutRefs.current.clear();
    };

    window.addEventListener('beforeunload', cleanup);
    return () => {
      window.removeEventListener('beforeunload', cleanup);
      cleanup();
    };
  }, []);

  const value: ErrorNotificationContextType = {
    notifications,
    showError,
    showWarning,
    showInfo,
    showSuccess,
    showApiError,
    removeNotification,
    clearAllNotifications,
    updateNotificationDuration
  };

  return (
    <ErrorNotificationContext.Provider value={value}>
      {children}
    </ErrorNotificationContext.Provider>
  );
};

export const useErrorNotification = () => {
  const context = useContext(ErrorNotificationContext);
  if (context === undefined) {
    throw new Error('useErrorNotification must be used within an ErrorNotificationProvider');
  }
  return context;
};